import { useState } from "react";
import { toast } from "sonner";
import { X, ArrowRight, Calendar, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const EventRegisterModal = ({ event, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/event-register", { event_id: event.id, ...form });
      toast.success(data.message || "Inscription confirmée !");
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
      data-testid="event-register-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8 relative">
          <button
            onClick={onClose}
            data-testid="modal-close"
            className="absolute top-4 right-4 h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>

          {success ? (
            <div className="text-center py-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#0B3D91]/10 flex items-center justify-center">
                <Calendar size={26} className="text-[#0B3D91]" />
              </div>
              <h3 className="mt-5 font-heading font-black text-2xl text-[#1C1C1C]">Inscription confirmée !</h3>
              <p className="mt-3 text-sm text-muted leading-relaxed">
                Un email de confirmation vous a été envoyé. Nous nous réjouissons de vous accueillir.
              </p>
              <button
                onClick={onClose}
                className="mt-6 btn-primary !py-2.5 !px-6 text-sm rounded-full"
                data-testid="modal-done"
              >
                Fermer
              </button>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B3D91]">
                {event.category}
              </div>
              <h3 className="mt-2 font-heading font-black text-2xl md:text-3xl tracking-tight text-[#1C1C1C]">
                {event.title}
              </h3>
              <div className="mt-4 space-y-1.5 text-sm text-muted">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-[#0B3D91]" /> {formatDate(event.date)}
                </div>
                {event.time && (
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-[#0B3D91]" /> {event.time}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-[#0B3D91]" /> {event.location}
                </div>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                    Nom complet <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="register-name"
                    className="mt-2 w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#0B3D91]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="register-email"
                    className="mt-2 w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#0B3D91]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                    Téléphone / WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="register-phone"
                    className="mt-2 w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#0B3D91]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                    Message (facultatif)
                  </label>
                  <textarea
                    rows={2}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="register-message"
                    className="mt-2 w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#0B3D91]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit"
                  className="w-full btn-primary rounded-full disabled:opacity-60"
                >
                  {loading ? "Inscription..." : "Confirmer mon inscription"} <ArrowRight size={16} />
                </button>
                <p className="text-xs text-muted text-center">
                  Un email de confirmation vous sera envoyé automatiquement.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventRegisterModal;
